export default class Modal {
    constructor() {
        this.modal = null;
        this.isOpen = false;
        this.currentType = null; // 'team' or 'event'
        this.currentPostId = null;
        this.currentModalElement = null; // Track the actual DOM element
        this.currentDepartment = null; // Track current department for team modals
        this.scrollPosition = 0;
        this.cleanupTimeout = null; // Track cleanup timeout to prevent race conditions
        this.init();
    }

    init() {
        this.createModal();
        this.bindEvents();
    }

    createModal() {
        // Create modal HTML structure
        this.modal = document.createElement('div');
        this.modal.className = 'modal-overlay fixed inset-0 z-[9999] opacity-0 pointer-events-none transition';
        this.modal.innerHTML = `
            <div class="modal-backdrop fixed inset-0 opacity-80 w-full h-full"></div>
            <section class="relative h-full flex items-center">
                <div class="container pb-3xl pt-3xl w-full">
                    <div class="modal-content flex flex-col gap-0 mx-auto relative transform scale-95 transition">
                        <div class="modal-body w-fit mx-auto flex flex-col sm:flex-row gap-0 relative">
                            <button class="modal-close absolute z-10 top-xs right-xs flex p-xs rounded-xxl border-0 outline-0 bg-blue-1 items-center w-max cursor-pointer">
                                <div class="w-4 h-4 rotate-180">
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M18 6L6 18M6 6l12 12" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        `;

        document.body.appendChild(this.modal);
        this.modalContent = this.modal.querySelector('.modal-content');
        this.modalBody = this.modal.querySelector('.modal-body');
        this.modalBackdrop = this.modal.querySelector('.modal-backdrop');
    }
    
    bindEvents() {
        // Close modal on backdrop click
        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) {
                this.close();
            }
        });

        // Close modal on close button click (using delegation to handle dynamic button)
        this.modal.addEventListener('click', (e) => {
            if (e.target.closest('.modal-close')) {
                this.close();
            }
        });

        // Close modal on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });

        // Listen for team card clicks
        document.addEventListener('click', (e) => {
            const teamButton = e.target.closest('[data-team-modal]');
            if (teamButton) {
                e.preventDefault();
                const postId = teamButton.getAttribute('data-team-modal');
                this.open(postId, 'team');
            }
        });

        // Listen for event card clicks
        document.addEventListener('click', (e) => {
            const eventButton = e.target.closest('[data-event-modal]');
            if (eventButton) {
                e.preventDefault();
                const postId = eventButton.getAttribute('data-event-modal');
                this.open(postId, 'event');
            }
        });

        // Listen for carousel prev/next clicks inside modal
        this.modal.addEventListener('click', (e) => {
            const prevButton = e.target.closest('[data-carousel="button-prev"]');
            const nextButton = e.target.closest('[data-carousel="button-next"]');

            if (prevButton) {
                e.preventDefault();
                this.navigateModal('prev');
            } else if (nextButton) {
                e.preventDefault();
                this.navigateModal('next');
            }
        });
    }
    
    open(postId, type = 'team', modalElement = null) {
        if (this.isOpen) return;

        // Clear any pending cleanup from a previous close
        if (this.cleanupTimeout) {
            clearTimeout(this.cleanupTimeout);
            this.cleanupTimeout = null;
        }

        this.currentType = type;
        this.currentPostId = postId;

        // Find the hidden modal content based on type
        const contentAttribute = type === 'team' ? 'data-team-content' : 'data-event-content';

        // If modalElement is provided, use it; otherwise find the first matching element
        let hiddenContent;
        if (modalElement) {
            hiddenContent = modalElement;
        } else {
            hiddenContent = document.querySelector(`[${contentAttribute}="${postId}"]`);
        }

        if (!hiddenContent) {
            console.error(`${type} content not found for ID:`, postId);
            console.log(`Available ${type} content elements:`, document.querySelectorAll(`[${contentAttribute}]`));
            return;
        }

        // Store the actual DOM element
        this.currentModalElement = hiddenContent;

        // Store department for team modals
        if (type === 'team') {
            this.currentDepartment = hiddenContent.getAttribute('data-department');
        }

        this.isOpen = true;

        // Apply type-specific Tailwind classes
        this.applyTypeStyles(type);

        // Copy content to modal while preserving the close button
        const closeButton = this.modalBody.querySelector('.modal-close');
        this.modalBody.innerHTML = hiddenContent.innerHTML;
        // Re-add the close button
        this.modalBody.appendChild(closeButton);

        // Show modal
        this.modal.classList.remove('pointer-events-none');
        this.modal.classList.add('opacity-100');
        this.modalContent.classList.remove('scale-95');
        this.modalContent.classList.add('scale-100');

        // Prevent body scroll - save current position first
        this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.scrollPosition}px`;
        document.body.style.width = '100%';
    }

    applyTypeStyles(type) {
        // Define different Tailwind classes for each modal type
        const styles = {
            team: {
                body: ['sm:flex-row'],
                backdrop: ['bg-blue-2']
            },
            event: {
                body: ['sm:flex-row'],
                backdrop: []
            }
        };

        const typeStyles = styles[type] || styles.team;

        // Apply content styles
        if (typeStyles.content) {
            this.modalContent.classList.add(...typeStyles.content);
        }

        // Apply body styles
        if (typeStyles.body) {
            this.modalBody.classList.add(...typeStyles.body);
        }

        // Apply backdrop styles
        if (typeStyles.backdrop) {
            this.modalBackdrop.classList.add(...typeStyles.backdrop);
        }

        // Store applied classes for cleanup
        this.appliedClasses = {
            content: typeStyles.content || [],
            body: typeStyles.body || [],
            backdrop: typeStyles.backdrop || []
        };
    }

    removeTypeStyles() {
        if (!this.appliedClasses) return;

        // Remove content styles
        if (this.appliedClasses.content) {
            this.modalContent.classList.remove(...this.appliedClasses.content);
        }

        // Remove body styles
        if (this.appliedClasses.body) {
            this.modalBody.classList.remove(...this.appliedClasses.body);
        }

        // Remove backdrop styles
        if (this.appliedClasses.backdrop) {
            this.modalBackdrop.classList.remove(...this.appliedClasses.backdrop);
        }

        this.appliedClasses = null;
    }
    
    close() {
        if (!this.isOpen) return;

        this.isOpen = false;

        // Hide modal with transition
        this.modal.classList.add('pointer-events-none');
        this.modal.classList.remove('opacity-100');
        this.modalContent.classList.add('scale-95');
        this.modalContent.classList.remove('scale-100');

        // Clear modal content after transition completes to prevent jump
        // Store the timeout reference so it can be cancelled if modal is reopened
        this.cleanupTimeout = setTimeout(() => {
            // Only clean up if modal is actually closed (prevents race condition)
            if (!this.isOpen) {
                // Preserve the close button when clearing content
                const closeButton = this.modalBody.querySelector('.modal-close');
                this.modalBody.innerHTML = '';
                if (closeButton) {
                    this.modalBody.appendChild(closeButton);
                }

                // Remove type-specific styles
                this.removeTypeStyles();
                this.currentType = null;
            }
            this.cleanupTimeout = null;
        }, 800); // Match custom transition duration from main.scss

        // Restore body scroll and position
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, this.scrollPosition);
    }

    navigateModal(direction) {
        if (!this.isOpen || !this.currentModalElement) return;

        const contentAttribute = this.currentType === 'team' ? 'data-team-content' : 'data-event-content';

        // Get all modal content elements of the current type
        let allModals;

        if (this.currentType === 'team' && this.currentDepartment) {
            // For team modals, only get modals from the same department
            allModals = Array.from(document.querySelectorAll(`[${contentAttribute}][data-department="${this.currentDepartment}"]`));
        } else {
            // For events or if no department, get all modals of this type
            allModals = Array.from(document.querySelectorAll(`[${contentAttribute}]`));
        }

        if (allModals.length <= 1) return; // Nothing to navigate to

        // Find current index using the actual DOM element
        const currentIndex = allModals.indexOf(this.currentModalElement);

        if (currentIndex === -1) return;

        // Calculate next index
        let nextIndex;
        if (direction === 'next') {
            nextIndex = (currentIndex + 1) % allModals.length; // Loop to start
        } else {
            nextIndex = (currentIndex - 1 + allModals.length) % allModals.length; // Loop to end
        }

        const nextModalElement = allModals[nextIndex];

        // Switch to the next modal element
        this.switchModal(nextModalElement);
    }

    switchModal(modalElement) {
        if (!modalElement) return;

        // Store the type before switching
        const type = this.currentType;
        const contentAttribute = type === 'team' ? 'data-team-content' : 'data-event-content';

        // Update current references
        this.currentModalElement = modalElement;
        this.currentPostId = modalElement.getAttribute(contentAttribute);

        // Store department for team modals
        if (type === 'team') {
            this.currentDepartment = modalElement.getAttribute('data-department');
        }

        // Update modal content without closing (smooth transition)
        const closeButton = this.modalBody.querySelector('.modal-close');
        this.modalBody.innerHTML = modalElement.innerHTML;
        // Re-add the close button
        this.modalBody.appendChild(closeButton);
    }
}